import type { AnalysisResponse } from "@patent-studio/api-types";
import { Panel } from "@/components/ui/Panel";
import { Explainer } from "@/components/ui/Explainer";
import { StepCard } from "@/components/patterns/StepCard";
import { toneClasses } from "@/components/ui/tone";
import { cn } from "@/lib/utils";
import {
  RULED_VERDICTS,
  VERDICT_LABELS,
  VERDICT_TONE,
  verdictMeaning,
} from "@/lib/verdict-labels";
import { plural } from "@/lib/plural";

/** The key the design offers beside the heading: what each verdict means over the references read. */
function VerdictKey({ read }: { read: number }) {
  return (
    <Explainer label="Key" chip="key" testId="verdict-key">
      {RULED_VERDICTS.map((verdict) => (
        <p key={verdict}>
          <b className="text-foreground">{VERDICT_LABELS[verdict]}.</b>{" "}
          {verdictMeaning(verdict, read)}
        </p>
      ))}
    </Explainer>
  );
}

/**
 * Novelty at a glance, the tab's third step: the scope of the run and the
 * three counts.
 *
 * Everything here is served. The counts are the server's, computed over the
 * standing verdicts, so they and the not-assessed figure always reconcile to
 * the rows beneath. The prior art figure is the references the run read: the
 * attorney's own documents, never what the disclosure admits.
 */
export function AtAGlance({ analysis }: { analysis: AnalysisResponse }) {
  const { counts, references_read: read, positions } = analysis;
  const assessed = positions.length - counts.not_assessed;
  return (
    <StepCard
      number={3}
      title="Novelty at a glance"
      testId="novelty-at-a-glance"
      aside={<VerdictKey read={read} />}
      intro={
        <>
          {plural(assessed, "position")} assessed against {read} prior art
          {counts.not_assessed > 0 && (
            <>
              {" · "}
              <span className="font-semibold text-foreground">
                {counts.not_assessed} not yet assessed
              </span>
            </>
          )}
        </>
      }
    >
      <dl className="grid gap-2.5 sm:grid-cols-3">
        {RULED_VERDICTS.map((verdict) => (
          // The count before its name, as the design reads a tile: the
          // figure first, in the verdict's colour.
          <Panel
            key={verdict}
            shape="row"
            pad="none"
            edge={VERDICT_TONE[verdict]}
            className="flex flex-row-reverse items-center justify-end gap-3 px-[18px] py-4"
          >
            <dt className="text-[15px] leading-tight font-bold">
              {VERDICT_LABELS[verdict]}
            </dt>
            <dd
              data-testid={`count-${verdict}`}
              className={cn(
                "font-mono text-[34px] leading-none font-extrabold tracking-[-0.03em] tabular-nums",
                toneClasses(VERDICT_TONE[verdict], "words"),
              )}
            >
              {counts[verdict]}
            </dd>
          </Panel>
        ))}
      </dl>
    </StepCard>
  );
}
